import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { CompareType, DataSelectType } from 'webapp/enums/enums';

import type { GroupSelected } from 'webapp/components/form/checkbox-group';
import FormCheckboxGroupComponent from 'webapp/components/form/checkbox-group';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import DsCompare from 'webapp/models-custom/ds-compare';
import type VisualizationBarChart from 'webapp/models-custom/visualization/bar-chart';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';

interface Args {
	blockM: Block;
	model: DataSelect;
}

type AdditionalLine =
	| DataSelectType.COMPARE
	| DataSelectType.GOAL
	| DataSelectType.AVERAGE
	| DataSelectType.TREND
	| DataSelectType.MEDIAN;

export default class BlockSettingsMetricAdditionalLinesComponent extends Component<Args> {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	get blockSetup(): BlockSetup {
		return this.args.blockM.blockSetup;
	}

	get visualization(): VisualizationLineChart | VisualizationBarChart {
		return this.blockSetup.visualization as VisualizationLineChart | VisualizationBarChart;
	}

	allOptions = [
		{ value: DataSelectType.COMPARE, label: 'Compare' },
		{ value: DataSelectType.AVERAGE, label: 'Average' },
		{ value: DataSelectType.TREND, label: 'Trend' },
		{ value: DataSelectType.MEDIAN, label: 'Median' },
		{ value: DataSelectType.GOAL, label: 'Goal' },
	];

	get options(): LabelValuePairNumeric[] {
		const isCompareDisabled = this.blockSetup.limitations.dateRange?.isAllTime;
		return this.allOptions.map((opt) => {
			return {
				value: opt.value,
				label: opt.label,
				disabled: opt.value === DataSelectType.COMPARE && isCompareDisabled,
			};
		});
	}

	@cached
	get additionalLineDataSelects(): DataSelect[] {
		return this.visualization.getSecondarySeriesForChartDs(this.args.model);
	}

	get selected() {
		return this.additionalLineDataSelects.map((item) => item.type) as AdditionalLine[];
	}

	@action
	update(value: GroupSelected): void {
		const dataSelect = this.args.model,
			newSelected = value as AdditionalLine[],
			addedTypes = newSelected.filter((type) => !this.selected.includes(type)),
			removedTypes = this.selected.filter((type) => !newSelected.includes(type));

		removedTypes.forEach((type) => {
			this.additionalLineDataSelects
				.filter((item) => item.type === type)
				.forEach((ds) => {
					this.blockSetup.deleteDataSelect(ds);
				});
			this.blockSetup.trackBlockPropertyMetricSettingsChangeEvent(dataSelect, {
				additional_line_disable: this.mmDataSelectsService.getTypeText(type),
			});
		});

		const hasCompares = dataSelect.compares.length > 0;

		addedTypes.forEach((type) => {
			if (!hasCompares && type === DataSelectType.COMPARE) {
				// no compares added, so just add first compare (Previous) and the block-setup will take care of creating the line too
				const dateRange = this.blockSetup.limitations?.dateRange;
				if (dateRange) {
					const newCompare = DsCompare.createFromFormula(null);
					if (dateRange.isSince) {
						// SinceToDate does not support Previous, so add Same period last year instead
						newCompare.updateFromTypeId(DsCompare.customPreviousOffsetYears, dateRange);
						newCompare.offset = -1;
					} else {
						newCompare.updateFromTypeId(DsCompare.customPreviousId, dateRange);
					}
					this.blockSetup.changeCompare(
						dataSelect,
						dataSelect.limitations.compare!,
						newCompare,
					);
				}
			} else if (type === DataSelectType.GOAL) {
				// add goal line for every goal compare
				const goalCompares = dataSelect.limitations.compareCMs.filter(
					(item) => item.isGoal,
				);
				if (goalCompares.length) {
					goalCompares.forEach((goalCompare) => {
						this.blockSetup.createGoalLineForCompare(dataSelect, goalCompare);
					});
				} else {
					// add an empty goal compare (or filled in case of single valid goal)
					const validGoals = this.mmGoalsService.getValidGoalsForMetricSetup(
						dataSelect.metricSetup,
					);

					const emptyCompare = DsCompare.createFromFormula(null);
					dataSelect.limitations.compareCMs = [
						...dataSelect.limitations.compareCMs,
						emptyCompare,
					];
					const goalCompare = DsCompare.createFromFormula({
						type: CompareType.GOAL,
						metricSetup:
							validGoals.length === 1
								? {
										key: validGoals[0]!.goalKeyLong,
										spaceAccessId: validGoals[0]!.mainMetricSetupSpaceAccessId,
									}
								: undefined,
					});
					this.blockSetup.changeCompare(dataSelect, emptyCompare, goalCompare);
				}
			} else {
				this.blockSetup.addSecondaryDataSelectForDataSelect(dataSelect, type);
			}
			this.blockSetup.trackBlockPropertyMetricSettingsChangeEvent(dataSelect, {
				additional_line_enable: this.mmDataSelectsService.getTypeText(type),
			});
		});
	}
	<template>
		<div class="row">
			<label>Show additional lines</label>

			<FormCheckboxGroupComponent
				class="radiobutton-switcher"
				@name="additional-lines"
				@selected={{this.selected}}
				@options={{this.options}}
				@onChange={{this.update}}
				@theme="dark-blue-alternate"
				@size="small"
			/>

		</div>
	</template>
}
